import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoonIcon, SunIcon, MenuIcon, CloseIcon } from './Icons';

const Header = ({ isDarkMode, onToggleDarkMode }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const { t, i18n } = useTranslation();

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };
    
    const navLinks = [
        { name: t('header.home'), path: '/' },
        { name: t('header.about'), path: '/despre' },
        { name: t('header.services'), path: '/servicii' },
        { name: t('header.resources'), path: '/resurse' },
        { name: t('header.blog'), path: '/blog' },
        { name: t('header.contact'), path: '/contact' }
    ];

    const isActivePage = (path) => {
        if (path === '/' && location.pathname === '/') return true;
        if (path !== '/' && location.pathname.startsWith(path)) return true;
        return false;
    };

    return (
        <header className="sticky top-0 z-50 bg-stone-50/90 dark:bg-stone-900/90 backdrop-blur-md border-b border-stone-200/80 dark:border-stone-800 transition-colors duration-300">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    
                    {/* Logo & Practitioner Identity */}
                    <Link 
                        to="/" 
                        className="group flex flex-col items-start focus:outline-none"
                    >
                        <span className="font-display text-2xl sm:text-2xl font-bold tracking-tight text-stone-900 dark:text-stone-100 group-hover:text-terracotta transition-colors duration-200">
                            Oana Tenea
                        </span>
                        <span className="text-[11px] sm:text-xs font-medium tracking-widest uppercase text-stone-500 dark:text-stone-400 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors">
                            Psihoterapeut CBT & Schema Therapy
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
                        {navLinks.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                    isActivePage(link.path)
                                        ? 'bg-stone-200/70 dark:bg-stone-800 text-terracotta dark:text-terracotta-light font-semibold shadow-xs' 
                                        : 'text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-stone-800/50'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </nav>

                    {/* Right utilities: Language, Dark mode, CTA */}
                    <div className="hidden md:flex items-center space-x-3">
                        {/* Language Switcher */}
                        <div className="flex items-center bg-stone-200/60 dark:bg-stone-800 p-0.5 rounded-full border border-stone-300/40 dark:border-stone-700">
                            <button
                                onClick={() => changeLanguage('ro')}
                                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all duration-200 ${
                                    (!i18n.language || i18n.language.startsWith('ro')) 
                                        ? 'bg-white dark:bg-stone-700 text-terracotta shadow-xs' 
                                        : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'
                                }`}
                                aria-label="Limba Română"
                            >
                                RO
                            </button>
                            <button
                                onClick={() => changeLanguage('en')}
                                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all duration-200 ${
                                    (i18n.language && i18n.language.startsWith('en')) 
                                        ? 'bg-white dark:bg-stone-700 text-terracotta shadow-xs' 
                                        : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'
                                }`}
                                aria-label="English Language"
                            >
                                EN
                            </button>
                        </div>

                        {/* Theme Toggle */}
                        <button
                            onClick={onToggleDarkMode}
                            className="p-2 rounded-full text-stone-600 dark:text-stone-300 hover:bg-stone-200/60 dark:hover:bg-stone-800 transition-colors"
                            aria-label="Toggle theme"
                        >
                            {isDarkMode ? <SunIcon /> : <MoonIcon />}
                        </button>

                        {/* Primary Appointment CTA */}
                        <Link 
                            to="/contact"
                            className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-terracotta hover:bg-terracotta-dark rounded-full shadow-soft hover:shadow-warm transition-all duration-200 transform hover:-translate-y-0.5"
                        >
                            <span>{t('header.contact')}</span>
                            <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </Link>
                    </div>

                    {/* Mobile Controls & Hamburger */}
                    <div className="flex lg:hidden items-center space-x-2">
                        {/* Mobile Language Switcher */}
                        <div className="flex items-center bg-stone-200/60 dark:bg-stone-800 p-0.5 rounded-full border border-stone-300/40 dark:border-stone-700">
                            <button
                                onClick={() => changeLanguage('ro')}
                                className={`px-2 py-0.5 text-xs font-semibold rounded-full transition-all ${
                                    (!i18n.language || i18n.language.startsWith('ro')) 
                                        ? 'bg-white dark:bg-stone-700 text-terracotta shadow-xs' 
                                        : 'text-stone-500 dark:text-stone-400'
                                }`}
                            >
                                RO
                            </button>
                            <button
                                onClick={() => changeLanguage('en')}
                                className={`px-2 py-0.5 text-xs font-semibold rounded-full transition-all ${
                                    (i18n.language && i18n.language.startsWith('en')) 
                                        ? 'bg-white dark:bg-stone-700 text-terracotta shadow-xs' 
                                        : 'text-stone-500 dark:text-stone-400'
                                }`}
                            >
                                EN
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 text-stone-700 dark:text-stone-200 hover:bg-stone-200/60 dark:hover:bg-stone-800 rounded-lg transition-colors"
                            aria-label="Toggle Navigation Menu"
                        >
                            {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Drawer */}
            {isMenuOpen && (
                <div className="lg:hidden bg-stone-50 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 px-6 py-6 space-y-3 animate-fade-in shadow-lg">
                    {navLinks.map(link => (
                        <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                                isActivePage(link.path)
                                    ? 'bg-stone-200/80 dark:bg-stone-800 text-terracotta font-semibold'
                                    : 'text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800/60'
                            }`}
                        >
                            {link.name}
                        </Link>
                    ))}

                    <div className="pt-4 border-t border-stone-200 dark:border-stone-800 flex items-center justify-between">
                        <span className="text-sm text-stone-500 dark:text-stone-400">Mod vizual</span>
                        <button
                            onClick={onToggleDarkMode}
                            className="p-2 rounded-full bg-stone-200/60 dark:bg-stone-800 text-stone-700 dark:text-stone-200"
                        >
                            {isDarkMode ? <SunIcon /> : <MoonIcon />}
                        </button>
                    </div>

                    <div className="pt-2">
                        <Link
                            to="/contact"
                            onClick={() => setIsMenuOpen(false)}
                            className="w-full inline-flex items-center justify-center px-5 py-3 text-center font-semibold text-white bg-terracotta rounded-xl shadow-warm hover:bg-terracotta-dark transition-colors"
                        >
                            {t('header.contact')}
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
